<template>
  <div class="container-fluid px-4">
    <div class="row">
      <div class="col-12">
        <div class="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center pt-3 pb-2 mb-3 border-bottom">
          <h1 class="h2">{{ $t('email.change') }}</h1>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col-md-6">
        <form @submit.prevent="submitChangeEmail" novalidate>
          <div class="form-group">
            <label for="currentEmail" class="form-label">{{ $t('email.current') }}</label>
            <input
              type="email"
              class="form-control"
              :class="{ 'is-invalid': errors.current_email }"
              id="currentEmail"
              v-model="form.current_email"
              @input="clearCurrentEmailError"
              :disabled="loading"
              placeholder="example@town.higashikawa.lg.jp"
            >
            <div v-if="errors.current_email" class="invalid-feedback">
              {{ Array.isArray(errors.current_email) ? errors.current_email[0] : errors.current_email }}
            </div>
          </div>

          <div class="form-group">
            <label for="newEmail" class="form-label">{{ $t('email.new') }}</label>
            <input
              type="email"
              class="form-control"
              :class="{ 'is-invalid': errors.new_email }"
              id="newEmail"
              v-model="form.new_email"
              @input="clearNewEmailError"
              :disabled="loading"
              placeholder="new-example@town.higashikawa.lg.jp"
            >
            <div v-if="errors.new_email" class="invalid-feedback">
              {{ Array.isArray(errors.new_email) ? errors.new_email[0] : errors.new_email }}
            </div>
          </div>

          <button
            type="submit"
            class="btn btn-primary btn-submit"
            :disabled="loading || !isFormValid"
          >
            <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
            {{ $t('email.button') }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { requestEmailChange } from '../../api/user/AuthApi.js';
import useFlashMessage from '../../composables/useFlashMessage.js';
import { colors } from '../../../consts/colors.js';

const router = useRouter();
const { t } = useI18n();
const { successMessage } = useFlashMessage();

const loading = ref(false);
const errors = ref({});

const form = reactive({
  current_email: '',
  new_email: ''
});

/**
 * Check if form is valid
 */
const isFormValid = computed(() => {
  if (!form.current_email || !form.new_email) {
    return false;
  }
  if (form.current_email === form.new_email) {
    return false;
  }
  return true;
});

/**
 * Check email format
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate form on client side
 */
const validateForm = () => {
  errors.value = {};
  let isValid = true;

  // Current email validation
  if (!form.current_email || form.current_email.trim() === '') {
    errors.value.current_email = t('email.validation.currentEmailRequired');
    isValid = false;
  } else if (!isValidEmail(form.current_email)) {
    errors.value.current_email = t('email.validation.currentEmailInvalid');
    isValid = false;
  }

  // New email validation
  if (!form.new_email || form.new_email.trim() === '') {
    errors.value.new_email = t('email.validation.newEmailRequired');
    isValid = false;
  } else if (!isValidEmail(form.new_email)) {
    errors.value.new_email = t('email.validation.newEmailInvalid');
    isValid = false;
  } else if (form.current_email === form.new_email) {
    errors.value.new_email = t('email.validation.sameEmail');
    isValid = false;
  }

  return isValid;
};

/**
 * Change email function
 */
const submitChangeEmail = async () => {
  if (!validateForm()) {
    // Errors are already set in errors.value and will be displayed inline
    return;
  }

  loading.value = true;
  errors.value = {};

  try {
    await requestEmailChange(
      form.current_email,
      form.new_email
    );

    successMessage(t('email.success'));

    // Clear form
    form.current_email = '';
    form.new_email = '';

    // Back to home
    router.push({ name: 'home' });

  } catch (error) {
    if (error.status === 422) {
      // Set errors for inline display
      errors.value = error.errors;
    } else {
      // For non-validation errors, set a generic error message
      errors.value = {
        current_email: t('validation.error')
      };
    }
  } finally {
    loading.value = false;
  }
};

/**
 * Functions to clear specific field errors
 */
const clearCurrentEmailError = () => {
  if (errors.value.current_email) {
    delete errors.value.current_email;
  }
};

const clearNewEmailError = () => {
  if (errors.value.new_email) {
    delete errors.value.new_email;
  }
};
</script>

<style lang="scss" scoped>
.form-group {
  margin-bottom: 1.5rem;

  .form-label {
    color: v-bind('colors.textPrimary');
    font-weight: 500;
    margin-bottom: 0.5rem;
    display: block;
  }

  .form-control {
    width: 100%;
    border: 1px solid v-bind('colors.borderMediumGray');
    border-radius: 4px;
    padding: 0.75rem;
    color: v-bind('colors.textPrimary');

    &:focus {
      border-color: v-bind('colors.btnBgColorPrimary');
      box-shadow: 0 0 0 0.2rem rgba(48, 111, 201, 0.25);
      outline: none;
    }

    &.is-invalid {
      border-color: v-bind('colors.error');
    }
  }

  .invalid-feedback {
    color: v-bind('colors.error');
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
  }
}

.btn {
  width: 100%;
  padding: 0.75rem;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-submit {
  background-color: v-bind('colors.btnBgColorPrimary');
  border-color: v-bind('colors.btnBgColorPrimary');
  color: v-bind('colors.white');
  margin-bottom: 1rem;
  border: none;

  &:hover:not(:disabled) {
    background-color: v-bind('colors.blue[600]');
    border-color: v-bind('colors.blue[600]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.blue[700]');
  }
}

.btn-back {
  background-color: v-bind('colors.white');
  color: v-bind('colors.btnBgColorPrimary');
  border: 1px solid v-bind('colors.borderMediumGray');

  &:hover:not(:disabled) {
    background-color: v-bind('colors.gray[50]');
  }

  &:active:not(:disabled) {
    background-color: v-bind('colors.gray[300]');
  }
}
</style>
